<template>
  <div class="container">
    <div class="row">
      <div class="col-xs-12">
        <template v-if="loaded">
          <div v-if="hasRecordsToShow">
            <h1>My Vehicles</h1>
            <p>
              We recommend you download your documents and save them securely for future reference
            </p>
            <div class="panel-group">
              <div
                v-for="record in recordsToShow"
                :key="`record${record.id}`"
                class="panel panel-primary"
              >
                <div class="panel-heading">
                  <h4 class="panel-title">
                    <span
                      v-if="record.customer.car_registration"
                      class="record-label"
                    >
                      <strong>Documents for vehicle:</strong>
                      {{ record.customer.car_registration }}
                    </span>
                    <span
                      v-if="record.customer.vehicle_stock_number"
                      class="record-label"
                    >
                      <strong>Vehicle stock number:</strong>
                      {{ record.customer.vehicle_stock_number }}
                    </span>
                  </h4>
                </div>
                <div class="panel-body form-inline">
                  <div
                    v-if="record.openDocumentRequests().length
                      && serviceLevelPaid
                      && record.suppression_requested_at === null"
                    class="alert alert-danger"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    There <span v-text="record.openDocumentRequests().length > 1 ? 'are' : 'is'" /> <strong v-text="record.openDocumentRequests().length" /> outstanding
                    document<span v-if="record.openDocumentRequests().length > 1">s</span> required
                  </div>
                  <div
                    v-if="record.documentsRequiringSignature().length
                      && serviceLevelPaid"
                    class="alert alert-danger"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    There <span v-text="record.documentsRequiringSignature().length > 1 ? 'are' : 'is'" /> <strong v-text="record.documentsRequiringSignature().length" />
                    outstanding document
                    signature<span v-if="record.documentsRequiringSignature().length > 1">s</span>
                    required
                  </div>
                  <div class="container-fluid">
                    <div class="row">
                      <ul class="col-sm-12 list-unstyled">
                        <li class="clearfix">
                          <label>Record created: </label>
                          <span v-text="moment(record.created_at).format('DD/MM/YYYY')" />
                        </li>
                        <li class="clearfix">
                          <label>Record status: </label>
                          <span v-text="record.status.name" />
                        </li>
                      </ul>
                    </div>
                    <div
                      v-if="record.suppression_requested_at === null"
                      class="row"
                    >
                      <ul
                        class="col-sm-12 list-unstyled"
                      >
                        <li class="text-right">
                          <a
                            :href="baseUrl(`/vehicles/${record.id}`)"
                            class="btn btn-primary"
                          >
                            Click to view documents
                          </a>
                          <button
                            :href="baseUrl(`/deals/${record.id}`)"
                            class="btn btn-danger"
                            @click="requestDelete(record.id)"
                          >
                            Click to request deletion
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div
                    v-if="record.suppression_requested_at !== null"
                    class="alert alert-danger alert-bottom"
                  >
                    Deletion requested
                  </div>
                </div>
              </div>
            </div>
            <nav
              v-if="totalPages > 1"
              aria-label="Page navigation"
              class="text-right"
            >
              <ul class="pagination">
                <li>
                  <a
                    :disabled="currentPage === 1"
                    aria-label="Previous"
                    href="#"
                    @click.prevent="(currentPage !== 1) ? goToPage(currentPage - 1) : null"
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </a>
                </li>
                <li
                  v-for="i in totalPages"
                  :key="`page${i}`"
                  :class="{active: currentPage === i}"
                >
                  <a
                    :disabled="currentPage === i"
                    href="#"
                    @click.prevent="(currentPage !== i) ? goToPage(i) : null"
                    v-text="i"
                  />
                </li>
                <li>
                  <a
                    :disabled="currentPage === totalPages"
                    aria-label="Next"
                    href="#"
                    @click.prevent="(currentPage !== totalPages) ? goToPage(currentPage + 1) : null"
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <div v-else>
            <p>There are currently no vehicles to show.</p>
          </div>
        </template>
        <span v-else>
          <font-awesome-icon
            icon="circle-notch"
            spin
          /> Loading...
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import _ from 'lodash';
import moment from 'moment';

export default {
  data() {
    return {
      records: [],
      loaded: false,
      totalPages: 0,
      currentPage: 1,
    };
  },
  computed: {
    hasRecordsToShow() {
      return this.recordsToShow.length;
    },
    recordsToShow() {
      return _.filter(this.records, this.canShowRecord);
    },
  },
  mounted() {
    this.fetchRecords(this);
  },
  methods: {
    goToPage(pageNum) {
      this.fetchRecords(this, pageNum);
    },
    fetchRecords: _.debounce((app, aPage) => {
      const page = aPage || 1;

      app.ApiService.fetchRecords(page)
        .then((response) => {
          const records = response.data.data || [];

          app.loaded = true;
          app.records = records;
          app.totalPages = response.data.last_page;
          app.currentPage = response.data.current_page;
        })
        .catch((error) => {
          app.alertBox({ type: 'error', message: error }).then(() => {
            app.records = [];
          }).catch();
        });
    }, 200),

    canShowRecord(record) {
      return !!record.documents.length || !!record.document_requests.length;
    },

    moment,

    requestDelete(recordId) {
      const app = this;

      app.alertBox({ type: 'confirm', message: 'Are you sure?' })
        .then(() => {
          app.ApiService.requestDelete(recordId)
            .then(() => {
              app.alertBox({ type: 'info', message: 'Request for deletion sent' }).catch();
              app.fetchRecords(app);
            })
            .catch((error) => {
              app.alertBox({ type: 'error', message: error }).catch();
            });
        }).catch();
    },
  },
};
</script>
<style type="text/scss" lang="scss" scoped="scoped">
@import 'resources/assets/sass/variables';

#app {
  .panel-group {
    .panel {
      .panel-body {
        .alert {
          margin : -15px -15px 15px -15px;
          border-radius: 0;
          padding: 5px 15px;
          &.alert-bottom {
            margin : 15px -15px -15px -15px;
            text-align: center;
            font-weight: 900;
          }
        }
      }
    }
  }
}
</style>
